import React from 'react';
import { Link } from 'react-router-dom';
import { useAiBudgetQuery } from 'src/api/queries/aiSettingsQueries';
import { ADD_CREDITS_ROUTE } from 'src/components/CreditAlertBanner/CreditAlertBanner';
import useRoleManager from 'src/hooks/useRoleManager';
import styles from './BudgetIndicator.module.scss';

function getResetLabel(period: 'daily' | 'weekly' | 'monthly'): string {
  switch (period) {
    case 'daily':
      return 'Resets at midnight UTC';
    case 'weekly':
      return 'Resets Monday UTC';
    case 'monthly':
      return 'Resets on the 1st';
  }
}

function isLowBudget(remaining: number | null, limit: number): boolean {
  if (limit === 0 || remaining === null) return false;
  return remaining / limit < 0.2;
}

interface BudgetBarProps {
  label: string;
  used: number;
  limit: number;
  remaining: number | null;
}

const BudgetBar: React.FC<BudgetBarProps> = ({ label, used, limit, remaining }) => {
  if (limit === 0) return null;
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const low = remaining !== null && remaining / limit < 0.2;
  const exhausted = remaining === 0;

  return (
    <div className={styles.budgetBar}>
      <div className={styles.budgetBarHeader}>
        <span className={styles.budgetBarLabel}>{label}</span>
        <span className={styles.budgetBarValue}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className={styles.budgetBarTrack}>
        <div
          className={`${styles.budgetBarFill} ${exhausted ? styles.exhausted : low ? styles.low : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

function getCreditExhaustedPeriod(
  credits: { daily: { remaining: number | null }; weekly: { remaining: number | null }; monthly: { remaining: number | null } },
): 'daily' | 'weekly' | 'monthly' | null {
  if (credits.daily.remaining === 0) return 'daily';
  if (credits.weekly.remaining === 0) return 'weekly';
  if (credits.monthly.remaining === 0) return 'monthly';
  return null;
}

export const BudgetIndicator: React.FC = () => {
  // Default retry policy on the hook handles transient 429/5xx from the
  // boot-time rate-limit race; we don't need to opt out.
  const { data: budget, isLoading } = useAiBudgetQuery();
  const { isAdmin } = useRoleManager();

  if (isLoading || !budget) return null;

  if (!budget.enabled) {
    return (
      <div className={styles.banner}>
        AI Agent is not available for your account.
      </div>
    );
  }

  const { credits } = budget;

  if (!credits.allowed) {
    if (credits.balance <= 0) {
      // The account's wallet is empty. Admins can fix that themselves; the
      // same role split as CreditAlertBanner (admins buy, others ask).
      return (
        <div className={`${styles.banner} ${styles.bannerExhausted}`} data-test="budget-exhausted">
          {isAdmin ? (
            <>
              You&apos;re out of credits.{' '}
              <Link className={styles.bannerLink} to={ADD_CREDITS_ROUTE}>Add credits</Link>
            </>
          ) : (
            "You're out of credits. Ask your account owner to add credits."
          )}
        </div>
      );
    }
    const creditPeriod = getCreditExhaustedPeriod(credits);
    const resetMsg = creditPeriod ? getResetLabel(creditPeriod) : '';
    return (
      <div className={`${styles.banner} ${styles.bannerExhausted}`}>
        {creditPeriod
          ? `${creditPeriod.charAt(0).toUpperCase() + creditPeriod.slice(1)} credit limit reached. ${resetMsg}.`
          : 'Credit limit reached.'}
      </div>
    );
  }

  const showCreditBars =
    isLowBudget(credits.daily.remaining, credits.daily.limit) ||
    isLowBudget(credits.weekly.remaining, credits.weekly.limit) ||
    isLowBudget(credits.monthly.remaining, credits.monthly.limit);

  if (!showCreditBars) return null;

  return (
    <div className={styles.budgetPanel}>
      <BudgetBar label="Daily Credits" used={credits.daily.used} limit={credits.daily.limit} remaining={credits.daily.remaining} />
      <BudgetBar label="Weekly Credits" used={credits.weekly.used} limit={credits.weekly.limit} remaining={credits.weekly.remaining} />
      <BudgetBar label="Monthly Credits" used={credits.monthly.used} limit={credits.monthly.limit} remaining={credits.monthly.remaining} />
    </div>
  );
};

export function useBudgetAllowed(): boolean {
  const { data } = useAiBudgetQuery();
  if (!data) return true;
  return data.enabled && data.credits.allowed;
}
